<script lang="ts">
    /**
     * Local component to replace <amp-lcd> https://github.pie.apple.com/amp-web/media-apps/blob/main/shared/components/src/components/PlayerLCD/PlayerLCD.svelte
     * Has a few more exported properties than PlayerLCDWrapper and uses a local implementation of LCDMetadata.
     */

    import { createEventDispatcher } from 'svelte';
    import { allowDrop } from '@amp/web-app-components/src/actions/allow-drop';
    import LCDMetadata from '@amp/web-app-components/src/components/LCDMetadata/LCDMetadata.svelte';
    import { buildSrc } from '@amp/web-app-components/src/components/Artwork/utils/srcset';
    import type { Realm, TimedMetadata } from './types';
    import ClassicalFallbackIcon from '@amp/web-app-components/assets/icons/music-classical.svg';

    /**
     * Used for configuring certain features of the LCD (initial load logo, LCD metadata format, etc.)
     */
    export let realm: Realm;

    /**
     * The now playing item.
     */
    export let item: any | null;

    /**
     * Timed metadata (i.e. streaming Radio stations)
     */
    export let timedMetadata: TimedMetadata | null = null;

    /**
     * Whether or not to show the brand logo when there is no item
     */
    export let showEmptyState: boolean = true;

    /**
     * Show LCD artwork overlay or not
     */
    export let showArtworkOverlay: boolean = true;

    /**
     * Is the queue item currently loading
     */
    export let isBuffering: boolean = false;

    /**
     * translate function provided by the parent app.
     */
    export let translateFn: (
        str: string,
        values?: Record<string, string | number>,
    ) => string;

    /**
     * Passthrough property for the LCD element
     * ContextualMenuConfig should match the types from the media-js components, but we can't import that
     * https://github.pie.apple.com/media-js/components/blob/b9a0f20c19c35036c5e6d883ee99c9f1c7eb6b1a/src/contextual-menu.d.ts
     */
    // TODO: Uncomment once we need to pass context menu config to this component.  Commented out
    //       for now as being unused causes this component to fail the Svelte linter.
    //       Ideally we just always pass in a context menu slot.
    //       rdar://139446102 (Onyx Web: Update shared PlayerLCD component for Music and Podcasts)
    // export let contextualMenuConfig: ContextualMenuConfig = null;

    /**
     * Badge, in this case, refers to the Favorites button, which is conditional
     * on the user being signed in with a Premium subscription. This flag just signals
     * that the element needs to render.
     */
    export let hasBadge: boolean = false;

    /**
     * Aria label for the artwork button
     */
    export let artworkButtonLabel: string = null;

    /**
     * Whether the LCD should be a drop area.
     * Works together with the on:dropOnLCD event.
     * @type {boolean}
     */
    export let dropEnabled: boolean = false;

    /**
     * Playback type of now playing item.  While this exists on `item`, it
     * needs to be passed in separately to force reactivity.  Setting a prop like
     *     $: playbackType = item?.playbackType
     * does not update the UI as the playback type changes.
     *
     * Based on enum values from
     * https://github.pie.apple.com/amp-web/components/blob/a8bb8c86398701686818c2e5208635bdd6a922e6/src/test-utils/index.ts#L185-L190
     */
    export let playbackType: number = 0;

    const dispatch = createEventDispatcher();

    let lcdElement: any;

    $: brandLogo =
        realm === 'podcasts'
            ? 'lcd-empty-artwork-podcasts'
            : 'apple-music-note';

    $: skipDirection = realm === 'podcasts' ? 'forward' : 'next';

    $: validMetadataLink = timedMetadata?.links.find((link) =>
        link.url.startsWith('https'),
    );
    $: isLiveRadioStation = item?.isLiveRadioStation;
    $: isPreview = playbackType === 1;

    $: artworkUrl = item?.artworkURL || item?.artwork?.template;
    $: artworkSrc = !!artworkUrl
        ? buildSrc(
              artworkUrl,
              {
                  width: 88,
                  height: 88,
                  fileType: 'jpg',
                  crop: 'bb',
                  quality: 60,
              },
              {},
          )
        : null;

    function handleMouseEnter() {
        dispatch('lcdMouseEntered');
    }

    function handleArtworkClick() {
        dispatch('launchFullScreenPlayer', { target: lcdElement });
    }
</script>

<div
    class="lcd-wrapper drop-reset"
    data-testid="player-lcd"
    use:allowDrop={!!dropEnabled && {
        dropEnabled,
        onDrop: (dropData) => dispatch('dropOnLCD', dropData),
    }}
>
    <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
        class={`lcd lcd__${realm}`}
        data-testid="lcd"
        class:lcd__initial={!item}
        class:lcd--buffering={isBuffering}
        bind:this={lcdElement}
        on:mouseenter={handleMouseEnter}
        tabindex="0"
    >
        <div class="lcd__artwork-container lcd__artwork" dir={document.dir}>
            <picture>
                {#if validMetadataLink}
                    <img
                        class="lcd__artwork-img"
                        role="presentation"
                        src={validMetadataLink.url}
                        alt=""
                        data-testid="player-lcd-timed-metadata-artwork"
                    />
                {:else if artworkSrc}
                    <img
                        class="lcd__artwork-img"
                        role="presentation"
                        src={artworkSrc}
                        alt=""
                        data-testid="player-lcd-artwork"
                    />
                {:else}
                    <div
                        class="lcd__artwork-container-empty"
                        data-testid="player-lcd-brand-logo"
                    >
                        {#if realm === 'classical'}
                            <div
                                class="lcd__artwork-img-empty lcd__artwork-img-empty--fallback-classical"
                            >
                                <ClassicalFallbackIcon aria-hidden="true" />
                            </div>
                        {:else}
                            <amp-icon
                                class="lcd__artwork-img-empty"
                                name={brandLogo}
                            />
                        {/if}
                    </div>
                {/if}
            </picture>

            {#if showArtworkOverlay}
                <div
                    class="lcd__artwork-overlay"
                    data-testid="player-lcd-artwork-overlay"
                >
                    <button
                        class="lcd__artwork-button"
                        data-testid="player-lcd-artwork-overlay-button"
                        on:click={handleArtworkClick}
                        aria-label={artworkButtonLabel}
                    >
                        <amp-icon name="arrow.up.left.and.arrow.down.right" />
                    </button>
                </div>
            {/if}
        </div>

        <div class="lcd__track-info-container lcd__active" aria-hidden={!item}>
            <LCDMetadata
                nowPlayingItem={item}
                hasSecondaryBadge={isPreview || isLiveRadioStation}
                {timedMetadata}
                {realm}
                {hasBadge}
                {translateFn}
                on:navigate
            >
                <slot name="context-menu" slot="context-menu" />
            </LCDMetadata>

            <div class="lcd__action-right" dir={document.dir}>
                {#if $$slots['lcd-action-right'] && hasBadge}
                    <slot name="lcd-action-right" />
                {/if}
            </div>
        </div>

        <div class="lcd__right-content">
            <div
                class="chrome-player__playback-controls chrome-player__playback-controls--compact"
                data-testid="lcd-right-content"
            >
                <div
                    class="chrome-player__compact-controls-base chrome-player__compact-controls-base--collapsed"
                >
                    <amp-playback-controls-play />
                    {#if skipDirection === 'next'}
                        <amp-playback-controls-item-skip direction="next" />
                    {:else}
                        <amp-playback-controls-skip
                            direction="forward"
                            fallbackSeconds={30}
                        />
                    {/if}
                </div>

                <div class="chrome-player__compact-controls-app-additions">
                    <div class="right-content-compact">
                        <slot name="right-content-compact" />
                    </div>
                </div>
            </div>
        </div>

        {#if isLiveRadioStation}
            <div class="lcd__badge-platter" data-testid="player-lcd-live-badge">
                {translateFn('MediaComponents.Common.Badge.Live')}
            </div>
        {:else if isPreview}
            <div
                class="lcd__badge-platter lcd__badge-platter--preview"
                data-testid="player-lcd-preview-badge"
            >
                {translateFn('MediaComponents.Video.Preview')}
            </div>
        {/if}

        <amp-lcd-progress class="lcd__progress" />

        <div
            class="lcd__track-info-container lcd__empty"
            aria-hidden={!!item || !showEmptyState}
        >
            <amp-icon
                class="lcd__track-info-empty"
                name="lcd-empty-logo"
                data-testid="player-lcd-empty-state"
            />
        </div>
    </div>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use '@amp/web-shared-styles/app/core/mixins/focus' as *;
    @use '@amp/web-shared-styles/app/core/mixins/absolute-center' as *;
    @use 'ac-sasskit/core/helpers' as *;
    @use 'ac-sasskit/core/locale' as *;
    @use 'amp/stylekit/core/mixins/browser-targets' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;

    .lcd-wrapper {
        position: relative;
        z-index: calc(
            var(--z-default) - 1
        ); // push down a layer in order to see LCD border

        &:global(.is-drag-over)::after {
            content: '';

            @include absolute-center;
            @include focus-shadow-inset;
        }
    }

    .chrome-player__playback-controls--compact {
        padding: 0 10px;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--playerLCDBGFill);

        @media (--range-xsmall-down) {
            :global(.chrome-player__initial)
                &
                .chrome-player__compact-controls-base {
                display: none;
            }
        }

        @media (--small) {
            display: none;
        }
    }

    .chrome-player__compact-controls-app-additions ::slotted(*) {
        margin-inline-start: 10px;
    }

    .chrome-player__compact-controls-base {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;

        :global(.chrome-player__expanded)
            &.chrome-player__compact-controls-base--collapsed {
            display: none;
        }

        amp-playback-controls-item-skip {
            --playback-control-icon-width: 28px;
            --playback-control-icon-height: 24px;
        }

        amp-playback-controls-skip {
            --playback-control-icon-width: 22px;
            --playback-control-icon-height: 22px;
        }
    }

    .lcd__right-content {
        grid-area: controls;
    }

    .lcd__classical .lcd__artwork-img-empty,
    .lcd__music .lcd__artwork-img-empty {
        width: 22px;
        height: 22px;
    }

    .lcd__artwork {
        margin: 0;
        padding: 0;
    }

    .lcd__toggle-text {
        @include visuallyhidden;
    }

    .lcd__artwork-img-empty {
        width: 44px;
        height: 44px;
        color: var(--playerMissingArtworkIcon);
    }

    .lcd__artwork-img-empty--fallback-classical {
        fill: var(--playerMissingArtworkIcon);

        :global(svg) {
            width: inherit;
            height: inherit;
            display: block;
            color: currentColor;
            transform: translateY(1.5px);
        }
    }

    .lcd__artwork-img {
        width: auto;
        height: 100%;
    }

    .lcd__artwork-container-empty {
        width: var(--lcd-height, 44px);
        height: var(--lcd-height, 44px);
        display: flex;
        align-items: center;
        justify-content: center;
        border-top-left-radius: 2px;
        border-bottom-left-radius: 2px;
        background-color: var(--playerMissingArtworkBg);

        amp-icon {
            fill: var(--playerMissingArtworkIcon);
        }
    }

    .lcd {
        // define these in host default state so that they are the fallbacks
        --lcd-artworkHoverBGColor: #{$lcd-artwork-hover-bg-color};
        --playback-controls-play-color: var(--systemSecondary);
        --skip-control-color: var(--systemSecondary);
        --playback-controls-play-color-hover: var(--systemPrimary);
        --skip-control-color-hover: var(--systemPrimary);
        --chromeBorderColor: #{$lcd-border-color};
        max-width: 100%;
        height: var(--lcd-height, 44px);
        border-radius: var(--lcd-border-radius, 2px);
        display: grid;
        grid-template-columns: auto minmax(40%, 900px) 1px auto;
        grid-template-rows: calc(var(--lcd-height, 44px) - 3px) 3px 0;
        grid-template-areas:
            'art    metadata badge  controls '
            'art    track    track  track'
            'border border   border border';
        position: relative;
        background-color: var(--lcd-bg-color, var(--playerLCDBGFill));
        color-scheme: light dark; // stylelint-disable-line property-no-unknown

        &:hover,
        &:hover .lcd__artwork-overlay {
            opacity: 1;
        }

        &:global(
                :has(.marquee--secondary:hover):has(.paddle-controls-container)
            )
            .lcd__badge-platter--preview {
            display: none;
        }

        @media (--small) {
            grid-template-columns: auto minmax(40%, 680px) 1px auto;
            box-shadow: inset 0 0 0 0.5px var(--chromeBorderColor);
        }

        @media (prefers-color-scheme: dark) {
            --lcd-bg-color: rgb(77, 77, 77);
        }

        @include target-safari {
            @media (--small) {
                border: 0.5px solid var(--chromeBorderColor);
                box-shadow: none;
            }
        }
    }

    .lcd__artwork-overlay {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        z-index: 2;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-color: var(--lcd-artworkHoverBGColor);
        box-shadow: 0 2px 28px swatch(shelfBg, dark);
        transition: opacity 150ms ease-out;
        opacity: 0;
        border-radius: inherit;

        button.lcd__artwork-button {
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 100%;
            transition: background-color 100ms ease-out;
            cursor: pointer;

            amp-icon {
                width: 16px;
                color: #fff;
            }
        }
    }

    .lcd__artwork-container {
        width: auto;
        min-width: var(--lcd-height, 44px);
        height: var(--lcd-height, 44px);
        grid-area: art;
        position: relative;
        background-color: var(--joeColor, var(--shelfBg));

        &::after {
            content: '';
            width: 100%;
            height: 100%;
            display: block;
            position: absolute;
            top: 0;
            left: 0;
            box-shadow: inset 0 0 0 0.5px var(--chromeBorderColor);

            @include target-safari {
                border-inline-end: 0.5px solid var(--chromeBorderColor);

                // Safari the playback thumbnail box-shadow and LCD border differently from Chrome and Firefox
                box-shadow: none;
            }
        }

        &,
        &::after,
        .lcd__artwork-img {
            --lcd-artwork-border-radius: 2px;

            @include target-safari {
                // Reduce corner radius to 1.5px in Safari, to avoid sub-pixel rounding error
                --lcd-artwork-border-radius: 1.5px;
            }
        }

        &[dir='ltr'] {
            &,
            &::after,
            .lcd__artwork-img {
                border-top-left-radius: var(--lcd-artwork-border-radius);
                border-bottom-left-radius: var(--lcd-artwork-border-radius);
            }
        }

        &[dir='rtl'] {
            &,
            &::after,
            .lcd__artwork-img {
                border-top-right-radius: var(--lcd-artwork-border-radius);
                border-bottom-right-radius: var(--lcd-artwork-border-radius);
            }
        }

        @media (--range-xsmall-down) {
            :global(chrome-player__expanded) & .lcd__artwork-img,
            :global(chrome-player__expanded) &::after .lcd__artwork-img {
                --lcd-artwork-border-radius: 0;
            }
        }

        @media (--small) {
            overflow: hidden;
        }
    }

    .lcd__artwork-img,
    .lcd__artwork-container-empty,
    .lcd__artwork-overlay,
    .lcd__artwork-container::after {
        @include target-safari {
            transform: translateY(-1px);
        }
    }

    .lcd__progress {
        --playerScrubberTrack: var(--systemQuaternary);
        --playerScrubberFill: var(--chromePlayerTrackElapsedColor);
        width: 100%;
        max-width: 900px;
        height: 3px;
        grid-area: track;
        position: relative;
    }

    .lcd.lcd__initial .lcd__progress {
        display: none;
    }

    .lcd.lcd__initial {
        // fill in background color where progress bar goes in pre-play state
        &::after {
            content: '';
            width: 100%;
            height: 3px;
            position: absolute;
            z-index: 0;
            top: 100%;
            right: 0;
            bottom: -3px;
            left: 0;
            background-color: var(--lcd-bg-color, var(--playerLCDBGFill));
        }
    }

    //
    // Track scrubber
    //
    .lcd__scrubber {
        width: 100%;
        position: absolute;
        bottom: 0;
        left: 0;
    }

    .lcd__progress-bar {
        width: 100%;
        height: 3px;
        position: absolute;
        bottom: 0;
        left: 0;
    }

    //
    // LCD text lines container & base styles
    //
    .lcd__track-info-container {
        position: relative;

        &[aria-hidden='true'] {
            display: none;
            visibility: hidden;
        }
    }

    .lcd__action-right {
        position: absolute;
        top: 3px;

        @include ltr {
            right: 4px;
        }

        @include rtl {
            left: 4px;
        }
    }

    .lcd__badge-platter {
        font: var(--caption-2-emphasized);
        margin-top: auto;
        margin-bottom: 4px;
        padding: 0 4px;
        grid-area: badge;
        z-index: 1;
        color: #fff;
        white-space: nowrap;
        border-radius: 3px;
        background-color: var(--systemTertiary);
        margin-inline-start: 8px;
        justify-self: end;

        @media (--small) {
            margin-inline-start: 0;
            margin-inline-end: 4px;
        }
    }

    .lcd__badge-platter--preview {
        background-color: var(--keyColor, var(--systemBlue));
    }

    .lcd__track-info {
        padding-top: 5px;
        width: 100%;
        height: 100%;
        display: inline-flex;
        box-sizing: border-box;
        flex-direction: column;
        align-items: center;
    }

    .lcd__track-info-empty {
        width: auto;
        height: 24px;
        color: var(--systemSecondary);
    }

    .lcd__empty {
        display: flex;
        align-items: center;
        justify-content: center;
    }
</style>
